import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import useHandleErrors from "../../utils/user.utils";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import useConfiguration from '../../utils/ConfigurationContext';

const PermissionsComponent = () => {
    const [permissionsData, setPermissionsData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const handleErrors = useHandleErrors();
    const [loading, setLoading] = useState(false);
    const storedToken = localStorage.getItem("token");
    const [originalPermissionsData, setOriginalPermissionsData] = useState([]);
    const hasFetchedRef = useRef(false);
    const config = useConfiguration();

    const fetchPermissionsData = async () => {
        setLoading(true);
        const path = "/permissionDetails";
        const URL = process.env.REACT_APP_BE_URL + path;
        try {
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });
            if (response.status === 200) {
                const formattedData = response.data.permissions.map((item, index) => ({
                    SNo: index + 1,
                    featureName: item.featureName,
                    admin: item.admin || "No",
                    superUser: item.superUser || "No",
                    user: item.user || "No",
                }));
                setPermissionsData(formattedData);
                setOriginalPermissionsData(formattedData);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxToggle = (featureName, role) => {
        const isMainFeature = mainFeatures.hasOwnProperty(featureName);
    
        let updatedPermissions = permissionsData.map((permission) => {
            // If the main feature is toggled
            if (isMainFeature && permission.featureName === featureName) {
                const toggleValue = permission[role] === "Yes" ? "No" : "Yes";
                // Update the main feature and all its sub-features
                if (
                    permission.featureName === featureName ||
                    mainFeatures[featureName].includes(permission.featureName)
                ) {
                    return {
                        ...permission,
                        [role]: toggleValue,
                    };
                }
            } else if (!isMainFeature && permission.featureName === featureName) {
                // If a sub-feature is toggled, only update that sub-feature
                return {
                    ...permission,
                    [role]: permission[role] === "Yes" ? "No" : "Yes",
                };
            }
            return permission;
        });
    
        // If a sub-feature is toggled, check if the main feature should be updated
        if (!isMainFeature) {
            const mainFeatureName = Object.keys(mainFeatures).find((mainFeature) =>
                mainFeatures[mainFeature].includes(featureName)
            );
            if (mainFeatureName) {
                // Check if any sub-features are still enabled
                const anySubFeaturesToggled = mainFeatures[mainFeatureName].some(
                    (subFeature) => {
                        const subFeaturePermission = updatedPermissions.find(
                            (permission) => permission.featureName === subFeature
                        );
                        return subFeaturePermission && subFeaturePermission[role] === "Yes";
                    }
                );

                // Update the main feature state
                updatedPermissions = updatedPermissions.map((permission) => {
                    if (permission.featureName === mainFeatureName) {
                        return {
                            ...permission,
                            [role]: anySubFeaturesToggled ? "Yes" : "No",
                        };
                    }
                    return permission;
                });
            }
        }
        setPermissionsData(updatedPermissions);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const path = "/permissions";
            const URL = process.env.REACT_APP_BE_URL + path;
            const payload = {
                permissions: permissionsData.map((permission) => ({
                    FeatureName: permission.featureName,
                    Admin: permission.admin === "Yes" ? "Yes" : "No",
                    SuperUser: permission.superUser === "Yes" ? "Yes" : "No",
                    User: permission.user === "Yes" ? "Yes" : "No",
                })),
            };

            const response = await axios.post(URL, payload, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
            });
            if (response.status === 200) {
                toast.success("Permissions updated successfully!", {
                    position: toast.POSITION.TOP_CENTER,
                });
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = (e) => {
        e.preventDefault();
        const resetPermissions = permissionsData.map((permission) => {
            const originalPermission = originalPermissionsData.find(
                (orig) => orig.featureName === permission.featureName
            );
            return {
                ...permission,
                admin:
                    permission.admin !== originalPermission.admin
                        ? originalPermission.admin
                        : permission.admin,
                superUser:
                    permission.superUser !== originalPermission.superUser
                        ? originalPermission.superUser
                        : permission.superUser,
                user:
                    permission.user !== originalPermission.user
                        ? originalPermission.user
                        : permission.user,
            };
        });
        setPermissionsData(resetPermissions);
    };

    const groupPermissionsByMainFeatures = (permissionsData, mainFeatures) => {
        const groupedData = [];

        // Add main features and their sub-features
        Object.keys(mainFeatures).forEach((mainFeature) => {
            // Find the main feature entry
            const mainFeatureEntry = permissionsData.find(
                (permission) => permission.featureName === mainFeature
            );

            if (mainFeatureEntry) {
                groupedData.push(mainFeatureEntry);

                // Add sub-features for this main feature
                mainFeatures[mainFeature].forEach((subFeature) => {
                    const subFeatureEntry = permissionsData.find(
                        (permission) => permission.featureName === subFeature
                    );
                    if (subFeatureEntry) {
                        groupedData.push(subFeatureEntry);
                    }
                });
            }
        });

        // Add any remaining features that are not part of mainFeatures
        permissionsData.forEach((permission) => {
            if (
                !groupedData.some(
                    (groupedPermission) =>
                        groupedPermission.featureName === permission.featureName
                )
            ) {
                groupedData.push(permission);
            }
        });

        return groupedData;
    };

    const mainFeatures = {
        Home: ["Dashboard", "My Profile", "Holidays", "Announcements"], // Added "Holidays" here
        "Employee Management": ["Onboard", "Employee Separation", "Offboard"],
        "Asset Management": [
            "Register Asset",
            "Asset Inventory",
            "Assign Asset",
            "Assigned Assets",
            "Master Report",
        ],
        "Self-service": [
            "Payslips",
            "Personal Information",
            "Medical Insurance",
            "Advance Salary",
            "Exit Request",
        ],
        Timesheet: [
            "Submit Timesheet",
            "View Timesheet",
            "Approve Timesheet",
            "Timesheet Report",
        ],
        "Leave Management": [
            "Submit Leave",
            "Leave Status",
            "Leave Report",
            "Leave Balance",
            "Leave Approval",
        ],
        Organisation: [
            "Employees",
            "Profile Search",
            "Policies",
            "View Policies",
            "Upload Policies",
            "HR",
            "HR List",
            "HR Connect",
            "Organisation Chart",
            "Complaint & Feedback",
            "Complaint and Feedback List",
            "Submit Complaint and Feedback",
            "Permissions",
            "Upload Center",
            "Advance Salary Requests",
            "OverAll Reports"
        ],
        "Expense Management": ["Submit Claims", "Claims Status", "Claims Report"],
        "Guest Registration": ["Register Guest", "View Guests"],
        "Support & Request": ["Submit Request", "Request Status", "Request Report"],
        "Project Management Center": ["Projects Dashboard", "Assign Project"],
        "Policies": ["View Policies", "Upload Policies"],
        "HR": ["HR List", "HR Connect"],
        "TravelDesk": [
            "Bookticket","Booking Requests", "My Orders" , "Tickets",
        ],
        "Complaint & Feedback": ["Complaint and Feedback List", "Submit Complaint and Feedback"],
        "Vendor Management": ["Vendor", "Invoices"],
        "Partner Management": ["Partner", "Partnerinvoices"],
        
    };

    const groupedPermissions = groupPermissionsByMainFeatures(permissionsData, mainFeatures);

    const filteredPermissions = groupedPermissions.filter((permission) =>
        permission.featureName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchPermissionsData();
            hasFetchedRef.current = true;
        }
    }, []);

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Permissions</h3>
                        </div>
                        <div className="col-auto">
                            <Link to={"/Payslips"} className="btn btn-primary mt-lg-0 mt-4">
                                <i className="ri-fingerprint-line"></i> Self-Service
                            </Link>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-8">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <div className="row align-items-center justify-content-center">
                                        <div className="col-lg-7 col-md-8 col-sm-10">
                                            <div className="container-fluid row-rounded mb-0">
                                                <div className="row px-2 py-0">
                                                    <div className="input-group mb-0">
                                                        <input
                                                            type="search"
                                                            className="form-control search-input"
                                                            placeholder="Keyword search..."
                                                            value={searchTerm}
                                                            onChange={handleSearchChange}
                                                        />
                                                        <div className="input-group-append">
                                                            <button
                                                                className="btn btn-primary"
                                                                type="button"
                                                                title="Search"
                                                                rel="tooltip"
                                                            >
                                                                <i className="ri-menu-search-line"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <small className="form-text text-muted text-center mt-0">
                                                <i className="ri-information-2-fill text-info"></i>{" "}
                                                Allows: Feature Name
                                            </small>
                                        </div>
                                    </div>
                                    <div
                                        className="table-responsive permissions-list mt-3"
                                        style={{
                                            maxHeight: "calc(100vh - 300px)",
                                            overflow: "auto",
                                        }}
                                    >
                                        <table className="table grid">
                                            <colgroup>
                                                <col />
                                                <col />
                                                <col className="admin-column-highlight" />
                                                <col className="super-user-column-highlight" />
                                                <col className="user-column-highlight" />
                                            </colgroup>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th width="30"></th>
                                                    <th>Feature Name</th>
                                                    <th className="text-center admin-column-highlight">
                                                        Admin
                                                    </th>
                                                    <th className="text-center super-user-column-highlight">
                                                        Super User
                                                    </th>
                                                    <th className="text-center user-column-highlight">
                                                        User
                                                    </th>
                                                    <th width="5"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPermissions.map((permission) => (
                                                    <React.Fragment key={permission.featureName}>
                                                        <tr>
                                                            <td>
                                                                {mainFeatures[permission.featureName] ? (
                                                                    (() => {
                                                                        const iconMap = {
                                                                            "Home": <i className="ri-home-4-fill" style={{ color: "#25b740" }} />,
                                                                            "Employee Management": <i className="ri-user-settings-fill" style={{ color: "#25b740" }} />,
                                                                            "Asset Management": <i className="ri-swap-2-fill" style={{ color: "#25b740" }} />,
                                                                            "Self-service": <i className="ri-fingerprint-2-fill" style={{ color: "#25b740" }} />,
                                                                            "Expense Management": <i className="ri-wallet-fill" style={{ color: "#25b740" }} />,
                                                                            "HR": <i className="ri-mail-send-line" style={{ color: "#25b740" }} />,
                                                                            "Complaint & Feedback": <i className="ri-user-received-fill" style={{ color: "#25b740" }} />,
                                                                            "Policies": <i className="ri-file-text-fill" style={{ color: "#25b740" }} />,
                                                                            "Project Management Center": <i className="ri-projector-fill" style={{ color: "#25b740" }} />,
                                                                            "Support & Request": <i className="ri-customer-service-2-line" style={{ color: "#25b740" }} />,
                                                                            "Organisation": <i className="ri-group-3-fill" style={{ color: "#25b740" }} />,
                                                                            "Leave Management": <i className="ri-calendar-todo-fill" style={{ color: "#25b740" }} />,
                                                                            "Timesheet": <i className="ri-calendar-schedule-fill" style={{ color: "#25b740" }} />,
                                                                            "Travel Desk": <i className="ri-plane-fill" style={{ color: "#25b740" }} />,
                                                                            "Vendor Management": <i className="ri-store-3-fil" style={{ color: "#25b740" }} />,
                                                                            "Partner Management": <i className="ri-team-fill" style={{ color: "#25b740" }} />,
                                                                        };
                                                                        return iconMap[permission.featureName] || (
                                                                            <i className="ri-folder-fill" style={{ color: "#25b740" }} />
                                                                        );
                                                                    })()
                                                                ) : (
                                                                    <i className="ri-arrow-right-line" style={{ color: "#004f7c" }} />
                                                                )}
                                                            </td>
                                                            <td
                                                                className={
                                                                    Object.keys(mainFeatures).includes(permission.featureName)
                                                                        ? "green-text font-weight-bold"
                                                                        : ""
                                                                }
                                                            >
                                                                {permission.featureName}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="position-relative btn-wrap">
                                                                    <div className="button-cover">
                                                                        <div className="btn-switch button r">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="checkbox"
                                                                                checked={permission.admin === "Yes"}
                                                                                onChange={() =>
                                                                                    handleCheckboxToggle(
                                                                                        permission.featureName,
                                                                                        "admin"
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div className="knobs"></div>
                                                                            <div className="layer"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="position-relative btn-wrap">
                                                                    <div className="button-cover">
                                                                        <div className="btn-switch button r">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="checkbox"
                                                                                checked={permission.superUser === "Yes"}
                                                                                onChange={() =>
                                                                                    handleCheckboxToggle(
                                                                                        permission.featureName,
                                                                                        "superUser"
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div className="knobs"></div>
                                                                            <div className="layer"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="position-relative btn-wrap">
                                                                    <div className="button-cover">
                                                                        <div className="btn-switch button r">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="checkbox"
                                                                                checked={permission.user === "Yes"}
                                                                                onChange={() =>
                                                                                    handleCheckboxToggle(
                                                                                        permission.featureName,
                                                                                        "user"
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div className="knobs"></div>
                                                                            <div className="layer"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            {mainFeatures[permission.featureName] && (
                                                                <td>
                                                                    <div className="position-relative btn-wrap">
                                                                        <div className="button-cover">
                                                                            <div className="btn-switch button r">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="checkbox"
                                                                                    onChange={() =>
                                                                                        handleCheckboxToggle(
                                                                                            permission.featureName,
                                                                                            "admin"
                                                                                        )
                                                                                    }
                                                                                    checked={mainFeatures[
                                                                                        permission.featureName
                                                                                    ].every((subFeature) => {
                                                                                        const subFeaturePermission =
                                                                                            permissionsData.find(
                                                                                                (p) =>
                                                                                                    p.featureName === subFeature
                                                                                            );
                                                                                        return (
                                                                                            subFeaturePermission &&
                                                                                            subFeaturePermission.admin ===
                                                                                            "Yes"
                                                                                        );
                                                                                    })}
                                                                                />{" "}
                                                                                Select All
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="card ww-app-bg">
                                <div className="card-body ">
                                    <span>
                                        <i className="ri-user-follow-line"></i>
                                    </span>
                                </div>
                            </div>
                            <div className="card mb-4">
                                <div className="card-body">
                                    <h4 className="mb-2">Know about your access?</h4>
                                    <p>
                                        Click the below icon and refer all our policies related to
                                        your needs.
                                    </p>
                                    <div className="row align-items-center justify-content-center">
                                        <div className="col-auto text-center">
                                            <a
                                                href={"/policies"}
                                                className="info-icon-box"
                                                title="Policies"
                                                rel="tooltip"
                                            >
                                                <i className="ri-file-text-line"></i>
                                            </a>
                                        </div>
                                    </div>
                                    <div className="row align-items-center border-top">
                                        <div className="col-12">
                                            <p className="mt-3">
                                                Quickly get in touch us for more queries.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-1">
                                            <i className="ri-mail-send-fill"></i>
                                        </div>
                                        <div className="col">{config?.adminEmail}</div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-1">
                                            <i className="ri-message-3-line"></i>
                                        </div>
                                        <div className="col">Chat with us</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row fixed-footer">
                <div className="col-lg-12 text-center">
                    <button type="reset" className="btn btn-light" onClick={handleCancel}>
                        Clear
                    </button>
                    <button type="submit" className="btn btn-primary" onClick={handleSubmit}>
                        Submit <i className="ri-checkbox-circle-line"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PermissionsComponent;